import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useChat } from './ChatContext';
import { FaUsers, FaUserFriends, FaPlus, FaCircle } from 'react-icons/fa';
import Cookies from 'js-cookie';
import { 
  List, 
  ListItem, 
  ListItemAvatar, 
  Avatar, 
  ListItemText, 
  Typography, 
  Divider,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'; // For online status indicator
import ChatControl from './ChatControl';
import GroupChatControl from './GroupChatControl';
import { useAuth } from '../AuthContext'; // Make sure this import exists
import PeopleIcon from '@mui/icons-material/People';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CloseIcon from '@mui/icons-material/Close';
import ClickAwayListener from '@mui/material/ClickAwayListener';

const Container = styled.div`
  background: #2d2f33;
  border-radius: 8px;
  padding: 16px;
  width: 300px;
  height: 100%;
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #404040;
`;

const ChatList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChatItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: ${props => props.isActive ? '#404040' : '#363636'};
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #404040;
  }
`;

const ChatName = styled.span`
  color: #ffffff;
  margin-left: 12px;
  flex: 1;
`;

const UnreadBadge = styled.span`
  background: #4a9bfc;
  color: white;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  min-width: 20px;
  text-align: center;
`;

const AddButton = styled.button`
  background: none;
  border: none;
  color: #4a9bfc;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #3580e3;
  }
`;

const UserList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 8px;
`;

const UserItem = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #363636;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #404040;
    transform: translateX(4px);
  }
`;

const OnlineStatus = styled.div`
  color: ${props => props.isOnline ? '#4caf50' : '#666'};
  margin-right: 8px;
  font-size: 10px;
`;

const UserName = styled.span`
  color: #ffffff;
  flex: 1;
`;

const ChatSelector = ({ onSelectConversation = () => {}, compact = false }) => {
  const { 
    activeChat, 
    chatGroups, 
    privateChats, 
    unreadMessages, 
    switchChat,
    startPrivateChat,
    fetchPrivateChats,
    fetchChatGroups
  } = useChat();
  const { user } = useAuth(); // Add this to get the current user from Auth context
  const [showChatControl, setShowChatControl] = useState(false);
  const [createGroupMode, setCreateGroupMode] = useState(false); // New state for group creation
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Ref to detect outside clicks
  const chatSelectorRef = useRef(null);
  
  // Fetch all conversations when component mounts
  useEffect(() => {
    const fetchAllConversations = async () => {
      setLoading(true);
      try {
        // These functions should be defined in ChatContext
        await fetchPrivateChats();
        await fetchChatGroups();
      } catch (error) {
        console.error("Error fetching conversations:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllConversations();
    // Set up periodic refresh
    const refreshInterval = setInterval(fetchAllConversations, 60000); // Refresh every minute
    
    return () => clearInterval(refreshInterval);
  }, [fetchPrivateChats, fetchChatGroups]);
  
  const fetchOnlineUsers = async () => {
    try {
      const token = Cookies.get("token");
      const response = await fetch(`${process.env.REACT_APP_STATUS_API_URL}/user/chat/online-users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnlineUsers(data.users);
      }
    } catch (error) {
      console.error("Error fetching online users:", error);
    }
  };

  useEffect(() => {
    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 30000);
    return () => clearInterval(interval);
  }, []);

  const isUserOnline = (username) => {
    return onlineUsers.some(user => user.username === username);
  };

  const handleUserClick = async (username) => {
    const existingChat = privateChats.find(chat => 
      chat.participants && chat.participants.includes(username)
    );

    if (existingChat) {
      switchChat(existingChat);
    } else {
      const newChat = await startPrivateChat(username);
      if (newChat) {
        switchChat(newChat);
      }
    }
  };

  // Modified function to handle user list button click
  const handleUserListClick = () => {
    setShowChatControl(true);
    setCreateGroupMode(false);
  };
  
  // Modified function to handle group creation click
  const handleCreateGroupClick = () => {
    setCreateGroupMode(true);
    setShowChatControl(true);
  };
  
  // Function to handle clicking away from the controls
  const handleClickAway = () => {
    if (showChatControl) {
      setShowChatControl(false);
      setCreateGroupMode(false);
    }
  };

  const handleChatCreated = (chat) => {
    // Make sure the chat is activated in both ChatContext and locally
    switchChat(chat);
    
    // Set the active chat locally if needed
    if (onSelectConversation) {
      onSelectConversation(getChatDisplayName(chat));
    }
    
    // Close the control panel
    setShowChatControl(false);
  };

  // Improve the chat selection handler
  const handleSelectChat = (chat) => {
    console.log("Selecting chat:", {
      id: chat.id,
      type: chat.type,
      name: chat.name,
      currentActive: activeChat?.id
    });
  
    // Prevent reselecting the same chat
    if (activeChat?.id === chat.id) {
      console.log("Chat already active, ignoring selection");
      return;
    }
  
    // Verify chat exists in the appropriate list
    const chatExists = chat.type === 'one-to-one'
      ? privateChats.some(p => p.id === chat.id)
      : chatGroups.some(g => g.id === chat.id);
  
    if (!chatExists) {
      console.error("Attempted to select non-existent chat:", chat);
      return;
    }
  
    // Switch chat in context
    switchChat(chat);
    
    // Update UI if needed
    if (onSelectConversation) {
      onSelectConversation(getChatDisplayName(chat));
    }
  };

  const getChatDisplayName = (chat) => {
    if (!chat) return 'Unknown';
    
    // For group chats, just use the name
    if (chat.type === 'group' || chat.type === 'channel') {
      return chat.name;
    }
    
    // For private chats, find the other participant
    if (chat.participants && Array.isArray(chat.participants)) {
      // Use user from the auth context instead of cookies
      const currentUser = user?.username;
      const otherUser = chat.participants.find(p => p !== currentUser);
      return otherUser || 'Unknown User';
    }
    
    return chat.name || 'Unknown Chat';
  };

  const isChatParticipantOnline = (chat) => {
    if (!chat || chat.type === 'group' || chat.type === 'channel') {
      return false;
    }
    
    // For private chats, check if the other participant is online
    if (chat.participants && Array.isArray(chat.participants)) {
      // Update here too
      const currentUser = user?.username;
      const otherUser = chat.participants.find(p => p !== currentUser);
      return isUserOnline(otherUser);
    }
    
    return false;
  };

  // Generate initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Add renderChatItem function before renderChatGroups
  const renderChatItem = (chat) => {
    const isActive = activeChat?.id === chat.id;
    const displayName = getChatDisplayName(chat);
    const unreadCount = unreadMessages[chat.id] || 0;
    const isOnlineUser = chat.type === 'one-to-one' && isChatParticipantOnline(chat);
  
    return (
      <ListItem
        key={chat.id}
        button
        onClick={() => handleSelectChat(chat)}
        sx={{
          borderRadius: 1,
          mb: 0.5,
          bgcolor: isActive ? 'rgba(74, 155, 252, 0.1)' : 'transparent',
          '&:hover': {
            bgcolor: isActive ? 'rgba(74, 155, 252, 0.15)' : 'rgba(255, 255, 255, 0.08)'
          }
        }}
      >
        <ListItemAvatar>
          <Box position="relative">
            <Avatar sx={{ bgcolor: isActive ? '#4a9bfc' : '#363636' }}>
              {getInitials(displayName)}
            </Avatar>
            {chat.type === 'one-to-one' && (
              <Box
                component={FiberManualRecordIcon}
                sx={{
                  position: 'absolute',
                  bottom: -2,
                  right: -2,
                  color: isOnlineUser ? '#4caf50' : '#666',
                  fontSize: 12,
                  backgroundColor: '#2d2f33',
                  borderRadius: '50%'
                }}
              />
            )}
          </Box>
        </ListItemAvatar>
        <ListItemText
          primary={displayName}
          secondary={chat.type === 'group' ? `${chat.participants?.length || 0} members` : null}
          primaryTypographyProps={{
            sx: {
              color: isActive ? '#4a9bfc' : '#e0e0e0',
              fontWeight: unreadCount > 0 ? 600 : 400
            }
          }}
          secondaryTypographyProps={{
            sx: { color: '#9e9e9e' }
          }}
        />
        {unreadCount > 0 && (
          <Box
            sx={{
              bgcolor: '#4a9bfc',
              color: 'white',
              borderRadius: '12px',
              px: 1,
              py: 0.5,
              minWidth: '20px',
              textAlign: 'center',
              fontSize: '0.75rem'
            }}
          >
            {unreadCount}
          </Box>
        )}
      </ListItem>
    );
  };

  // Modify renderChatGroups function to clearly identify group type
  const renderChatGroups = () => {
    // Create a map to track which IDs we've seen to avoid duplicates
    const seenIds = new Set();
    const uniqueGroups = chatGroups.filter(group => {
      // Only keep items we haven't seen before
      if (seenIds.has(group.id)) {
        console.warn(`Duplicate group detected: ${group.id} - ${group.name}`);
        return false;
      }
      seenIds.add(group.id);
      
      // Also make sure it's really a group (not 'one-to-one')
      return group.type !== 'one-to-one';
    });
    
    if (uniqueGroups.length === 0) return null;
    
    return (
      <>
        {/* Group header */}
        {!compact && (
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#9e9e9e', 
              px: 2, 
              py: 1, 
              mt: 1,
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <FaUsers size={14} /> Groups & Channels ({uniqueGroups.length})
          </Typography>
        )}
        
        {/* Render the filtered list */}
        {uniqueGroups.map(renderChatItem)}
      </>
    );
  };

  // Similar function for private chats
  const renderPrivateChats = () => {
    // Create a set of IDs to avoid duplicates
    const seenIds = new Set();
    const uniqueChats = privateChats.filter(chat => {
      if (seenIds.has(chat.id)) {
        console.warn(`Duplicate private chat detected: ${chat.id}`);
        return false;
      }
      seenIds.add(chat.id);
      
      // Make sure it's really a private chat
      return chat.type === 'one-to-one' || !chat.type;
    });
    
    if (uniqueChats.length === 0) return null;
    
    return (
      <>
        {/* Private chats header */}
        {!compact && (
          <Typography 
            variant="subtitle2" 
            sx={{ 
              color: '#9e9e9e', 
              px: 2, 
              py: 1, 
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <FaUserFriends size={14} /> Private Messages ({uniqueChats.length})
          </Typography>
        )}
        
        {/* Render the filtered list */}
        {uniqueChats.map(renderChatItem)}
      </>
    );
  };

  if (loading) {
    return (
      <Box sx={{ 
        height: '100%',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#2d2f33'
      }}>
        <Typography sx={{ color: '#e0e0e0' }}>Loading conversations...</Typography>
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Box 
        ref={chatSelectorRef}
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          backgroundColor: '#2d2f33',
          // Add custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#2d2f33',
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 255, 255, 0.15)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'rgba(255, 255, 255, 0.25)',
          },
          // For Firefox
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255, 255, 255, 0.15) #2d2f33',
          position: 'relative',
        }}
      >
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ color: '#e0e0e0' }}>
            Conversations
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="User List">
              <IconButton 
                size="small" 
                onClick={handleUserListClick}
                sx={{ 
                  color: '#9e9e9e',
                  '&:hover': { 
                    color: '#4a9bfc',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <PeopleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            
            <Tooltip title="Create Group">
              <IconButton 
                size="small" 
                onClick={handleCreateGroupClick}
                sx={{ 
                  color: '#9e9e9e',
                  '&:hover': { 
                    color: '#4caf50',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <GroupAddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Show overlay controls when active */}
        {showChatControl && (
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              backgroundColor: '#2d2f33',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden'
            }}
          >
            {/* Header with close button */}
            <Box sx={{ 
              p: 1, 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              borderBottom: '1px solid rgba(255,255,255,0.1)'
            }}>
              <Typography variant="h6" sx={{ color: '#e0e0e0' }}>
                {createGroupMode ? 'Create Group' : 'User List'}
              </Typography>
              <IconButton 
                size="small" 
                onClick={() => {
                  setShowChatControl(false);
                  setCreateGroupMode(false);
                }}
                sx={{ 
                  color: '#9e9e9e',
                  '&:hover': { 
                    color: '#ff5252',
                    backgroundColor: 'rgba(255, 255, 255, 0.05)'
                  }
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            
            {/* Content area */}
            <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
              {!createGroupMode ? (
                <ChatControl 
                  onChatCreated={handleChatCreated} 
                  onClose={() => setShowChatControl(false)} 
                />
              ) : (
                <GroupChatControl 
                  onGroupCreated={handleChatCreated} 
                  onClose={() => {
                    setShowChatControl(false);
                    setCreateGroupMode(false);
                  }} 
                />
              )}
            </Box>
          </Box>
        )}

        {/* Regular content shown when no overlay is active */}
        {!showChatControl && (
          <List sx={{ 
            p: compact ? 0.5 : 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: compact ? 'center' : 'stretch',
          }}>
            {renderPrivateChats()}
            {renderChatGroups()}
            
            {privateChats.length === 0 && chatGroups.length === 0 && !loading && (
              <Typography 
                variant="body2" 
                sx={{ color: '#9e9e9e', py: 2, textAlign: 'center' }}
              >
                No conversations yet. Start a new chat!
              </Typography>
            )}
          </List>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default ChatSelector;
